/**
 * Workshop status vocabulary (BON-197, Round 1). TWO independent axes, kept
 * separate so a workshop never shows a conflated/double badge:
 *
 *   Axis A — Workshop status (lifecycle-derived) — `resolveWorkshopStatus`:
 *     | lifecycle === 'cancelled' (any date) | Cancelled (red dot)  |
 *     | lifecycle === 'draft'                | Draft (amber dot)    |
 *     | lifecycle === 'disabled' (any date)  | Disabled (blue dot)  |
 *     | active + future (startsAt >= now)    | Active (green)       |
 *     | active + past  (startsAt < now)      | Completed (derived)  |
 *
 *   Axis B — Pacing (sales-pace signal, ONLY for active + future) —
 *   `paceToStatus`: On track / Behind Pace / Sold out.
 *
 * Axis A is DERIVED ("Completed" is not a stored lifecycle; it is active + a
 * past `startsAt`). Pacing vocabulary must NEVER
 * appear on a past row — sold-out-ness there is conveyed by the Fill column.
 *
 * `resolveWorkshopStatus` + `WorkshopStatusPill` render Axis A. Both are PURE /
 * side-effect-free (pass `now`) so they're the single unit-tested source.
 */
import type { Workshop, WorkshopPaceStatus } from '../../../../api/workshops';
import { DANGER_RED, DotPill, StatusPill, type StatusTone } from '../../primitives';

/** Map the BE sales-pace bucket to the StatusPill tone + label (Axis B, BON-168).
 *  Lives here (the shared status module) so callers reuse it without a
 *  dependency cycle; re-exported from `upcomingWorkshopsFormat` for stability.
 *  Only meaningful for active + future workshops. */
export function paceToStatus(pace: WorkshopPaceStatus | undefined): {
  tone: StatusTone;
  label: string;
} {
  switch (pace) {
    case 'sold_out':
      return { tone: 'sold-out', label: 'Sold out' };
    case 'behind_pace':
      return { tone: 'at-risk', label: 'Behind Pace' };
    case 'on_track':
    default:
      return { tone: 'on-track', label: 'On track' };
  }
}

/** How an Axis-A status renders. `pill` reuses the shared {@link StatusPill}
 *  tones (Active); `dot` is a colored-dot pill for the amber Draft / red
 *  Cancelled / blue Disabled / neutral Completed states (no matching
 *  StatusPill tone). */
export type StatusDescriptor =
  | { kind: 'pill'; tone: StatusTone; label: string }
  | { kind: 'dot'; label: string; color: string; background: string };

/** The subset of a workshop Axis A depends on: lifecycle + the past/future
 *  boundary (`startsAt` vs `now`). Pacing/fill are NOT inputs to Axis A. */
export interface WorkshopStatusInput {
  lifecycle: Workshop['lifecycle'];
  /** ISO datetime string. */
  startsAt: string;
}

/**
 * Axis A — resolve a workshop's lifecycle-derived status. Pure; pass the
 * caller's `now` so the Active/Completed boundary is explicit and testable.
 *
 *   cancelled → Cancelled · draft → Draft · disabled → Disabled ·
 *   active+future → Active · active+past → Completed.
 */
export function resolveWorkshopStatus(w: WorkshopStatusInput, now: Date): StatusDescriptor {
  if (w.lifecycle === 'cancelled') {
    return {
      kind: 'dot',
      label: 'Cancelled',
      color: DANGER_RED,
      background: 'rgba(192,57,43,.12)',
    };
  }
  if (w.lifecycle === 'draft') {
    return {
      kind: 'dot',
      label: 'Draft',
      color: 'var(--nb-warn)',
      background: 'var(--nb-warn-soft)',
    };
  }
  if (w.lifecycle === 'disabled') {
    return {
      kind: 'dot',
      label: 'Disabled',
      color: 'var(--nb-info)',
      background: 'var(--nb-info-soft)',
    };
  }
  // Active in the DB. Future → Active (upcoming); past → Completed (derived).
  const isFuture = new Date(w.startsAt).getTime() >= now.getTime();
  if (isFuture) {
    return { kind: 'pill', tone: 'active', label: 'Active' };
  }
  return {
    kind: 'dot',
    label: 'Completed',
    color: 'var(--nb-subtle)',
    background: 'rgba(120,120,120,.12)',
  };
}

/**
 * Axis-A status pill. Drop-in for any surface that shows a workshop's
 * lifecycle-derived status (venue table past rows + detail-page header).
 * `now` defaults to the render instant.
 */
export function WorkshopStatusPill({
  workshop,
  now = new Date(),
}: {
  workshop: WorkshopStatusInput;
  now?: Date;
}) {
  const status = resolveWorkshopStatus(workshop, now);
  if (status.kind === 'dot') {
    return <DotPill label={status.label} color={status.color} background={status.background} />;
  }
  return <StatusPill tone={status.tone} label={status.label} />;
}
